import {
  CHANNEL_META,
  CREDIT_COST,
  PLAN_META,
  TRIAL_DAYS,
  TRIAL_PACK,
  TRIAL_REEL,
  type BillingInterval,
  type Channel,
  type MediaMode,
  type PlanKey,
  type PostFormat,
} from '../constants.js';
import { reelPrice } from '../reel-storyboard.js';
import type { GenerationTarget } from './generator.js';

export type PlanCheck = { ok: true } | { ok: false; reason: string };

export function channelsAllowed(plan: PlanKey, channels: readonly Channel[]): PlanCheck {
  const allowed = PLAN_META[plan].channels;
  const blocked = channels.filter((c) => !allowed.includes(c));
  if (blocked.length) {
    return {
      ok: false,
      reason: `${blocked.map((c) => CHANNEL_META[c].label).join(', ')} ${blocked.length > 1 ? 'are' : 'is'} not included in ${PLAN_META[plan].name}. Upgrade to Pro for all channels.`,
    };
  }
  return { ok: true };
}

/**
 * The tier whose channels a property gets: its own. Off the plan (null) it has Starter's, the
 * floor every account starts from, so Instagram and Facebook can be connected before a tier is
 * chosen and Pro-only platforms never can.
 */
export function channelTier(plan: PlanKey | null | undefined): PlanKey {
  return plan ?? 'starter';
}

/** The channels a property may connect or post to. */
export function propertyChannels(plan: PlanKey | null | undefined): readonly Channel[] {
  return PLAN_META[channelTier(plan)].channels;
}

export type PlanPrice = {
  interval: BillingInterval;
  /** Charged per billing period. */
  totalCents: number;
  /** What the host sees: the period price spread over its months. */
  perMonthCents: number;
};

export function planPrice(plan: PlanKey, interval: BillingInterval): PlanPrice {
  const meta = PLAN_META[plan];
  if (interval === 'year')
    return {
      interval,
      totalCents: meta.yearlyPriceCents,
      perMonthCents: monthlyEquivalentCents(plan),
    };
  return { interval, totalCents: meta.priceCents, perMonthCents: meta.priceCents };
}

export function trialEndsAt(from: Date, days = TRIAL_DAYS): Date {
  const d = new Date(from);
  d.setUTCDate(d.getUTCDate() + days);
  return d;
}

/** The annual price spread over twelve months, to the cent. Derived, never typed. */
export function monthlyEquivalentCents(plan: PlanKey): number {
  return Math.round(PLAN_META[plan].yearlyPriceCents / 12);
}

/** What annual saves against twelve monthly payments; the percent rounds down so it is always true. */
export function annualSavings(plan: PlanKey): { cents: number; percent: number } {
  const monthly = PLAN_META[plan].priceCents * 12;
  const cents = monthly - PLAN_META[plan].yearlyPriceCents;
  return { cents, percent: Math.floor((cents / monthly) * 100) };
}

/** What the card is charged when the trial ends: the interval price less the trial pack credit. */
export function dayChargeCents(plan: PlanKey, interval: BillingInterval): number {
  return planPrice(plan, interval).totalCents - TRIAL_PACK.priceCents;
}

/**
 * How many cards a run draws: a carousel draws one per pinned photo when the host pinned two or
 * more (four otherwise), or the cards a plan targets; every other format is one picture. The
 * generator and the charge both read this, so the price and the run cannot drift.
 */
export function cardCountFor(format: PostFormat, pinnedCount: number, planned?: number): number {
  if (format !== 'Carousel') return 1;
  return planned ?? (pinnedCount >= 2 ? pinnedCount : 4);
}

/**
 * Credits a generation spends, from what it makes: words only keep the picture; a Reel is one
 * price, the trial-length reel's, unless the host's own video is the reel; a picture costs by how
 * far it departs from the photo, per card drawn.
 */
export function creditSpend(input: {
  format: PostFormat;
  mediaMode: MediaMode;
  target?: GenerationTarget;
  /** The host pinned their own video: it is prepared as it is and captioned. */
  ownVideo?: boolean;
  /** Cards the run draws (`cardCountFor`); pictures are charged per card. Default 1. */
  cards?: number;
}): number {
  if (input.target === 'copy' || input.ownVideo) return CREDIT_COST.rewrite;
  if (input.format === 'Reel') return reelPrice(TRIAL_REEL.durationSeconds).total;
  const cards = input.cards ?? 1;
  switch (input.mediaMode) {
    case 'photos':
      return CREDIT_COST.still * cards;
    case 'enhance':
    case 'reimagine':
      return CREDIT_COST.enhancedStill * cards;
    case 'imagine':
      return CREDIT_COST.imaginedStill * cards;
  }
}
